/** @jsxImportSource solid-js */

import { Component, For, Show, createSignal, untrack, JSX } from 'solid-js';
import { translate } from '@fulcrum/lib';
import AttachmentListItem from './AttachmentListItem';

const minListSize = 3;
const maxVisibleInEdit = 4;

export interface ICommentAttachmentListProps {
  readonly attachments: readonly unknown[];
  onRemoveAttachment?(index: number): void;
  readonly displayMore?: boolean;
  setDisplayMore?(mode: boolean): void;
  readonly uploading: boolean;
  readonly accessToken?: string;
}

const CommentAttachmentList: Component<ICommentAttachmentListProps> = (
  props
) => {
  const [displayMore, setDisplayMore] = createSignal(
    // eslint-disable-next-line solid/reactivity
    props.displayMore || false
  );

  const handleToggleDisplay = (event: MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();

    const newDisplayMore = !displayMore();
    setDisplayMore(newDisplayMore);

    const customEvent = new CustomEvent('display-more-toggle', {
      detail: {
        displayMore: newDisplayMore,
        originalEvent: event,
      },
      bubbles: true,
    });
    (event.target as HTMLElement).dispatchEvent(customEvent);

    if (props.setDisplayMore) {
      props.setDisplayMore(newDisplayMore);
    }
  };

  const handleRemoveAttachment = (index: number): void => {
    const customEvent = new CustomEvent('attachment-remove', {
      detail: { index },
      bubbles: true,
    });
    document.dispatchEvent(customEvent);

    if (props.onRemoveAttachment) {
      props.onRemoveAttachment(index);
    }
  };

  const renderSpinner = (): JSX.Element => (
    <li class="list pt-1">
      <i class="icon-font tc-icon-spinner-dark backgroundNoRepeat" />
    </li>
  );

  const visibleAttachments = (): readonly unknown[] => {
    const attachments = props.attachments || [];
    if (!Array.isArray(attachments) || attachments.length <= minListSize) {
      return attachments;
    }
    return displayMore() ? attachments : attachments.slice(0, minListSize);
  };

  const remainingAttachments = (): readonly unknown[] => {
    const attachments = props.attachments || [];
    if (
      !Array.isArray(attachments) ||
      attachments.length <= minListSize ||
      displayMore()
    ) {
      return [];
    }
    return attachments.slice(minListSize);
  };

  const showToggleButton = (): boolean => {
    const attachments = props.attachments || [];
    return Array.isArray(attachments) && attachments.length > minListSize;
  };

  return (
    <ul class="list mb-1" id="attachment-list">
      <Show
        when={props.uploading}
        fallback={
          <>
            <For each={visibleAttachments()}>
              {(attachment, index) => (
                <AttachmentListItem
                  attachment={untrack(
                    () =>
                      attachment as {
                        readonly imgUrl?: string;
                        readonly type: string;
                        readonly title: string;
                      }
                  )}
                  index={index()}
                  onRemoveAttachment={handleRemoveAttachment}
                  accessToken={props.accessToken}
                />
              )}
            </For>
            <Show when={showToggleButton()}>
              <li class="list-item">
                <button
                  class="button link-primary"
                  onClick={handleToggleDisplay}
                  type="button"
                >
                  {displayMore()
                    ? translate('TC.Common.ShowLess')
                    : translate('TC.Common.ShowAll')}
                </button>
              </li>
            </Show>
            <Show when={displayMore()}>
              <For each={remainingAttachments()}>
                {(attachment, index) => (
                  <AttachmentListItem
                    attachment={untrack(
                      () =>
                        attachment as {
                          readonly imgUrl?: string;
                          readonly type: string;
                          readonly title: string;
                        }
                    )}
                    index={index() + minListSize}
                    onRemoveAttachment={handleRemoveAttachment}
                    accessToken={props.accessToken}
                  />
                )}
              </For>
            </Show>
          </>
        }
      >
        {renderSpinner()}
      </Show>
    </ul>
  );
};

export default CommentAttachmentList;

// Alternative simplified version for editing mode
export const AttachmentListInEdit: Component<
  Pick<
    ICommentAttachmentListProps,
    'attachments' | 'onRemoveAttachment' | 'accessToken'
  >
> = (props) => {
  const handleRemoveAttachment = (index: number): void => {
    if (props.onRemoveAttachment) {
      props.onRemoveAttachment(index);
    }
  };

  return (
    <ul class="list mb-1">
      <For each={props.attachments.slice(0, maxVisibleInEdit)}>
        {(attachment, index) => (
          <AttachmentListItem
            attachment={
              attachment as {
                readonly imgUrl?: string;
                readonly type: string;
                readonly title: string;
              }
            }
            index={index()}
            onRemoveAttachment={handleRemoveAttachment}
            accessToken={props.accessToken}
          />
        )}
      </For>
      <Show when={props.attachments.length > maxVisibleInEdit}>
        <li class="list-item">
          <small class="text-meta">
            +{props.attachments.length - maxVisibleInEdit} more
          </small>
        </li>
      </Show>
    </ul>
  );
};
